import React from 'react'
import { Button, Card, Form, Grid, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd'
import { Link } from 'react-router-dom'
import { listCaptains, createCaptain } from '../../api/adminCaptains'
import { listDeliveryLocations } from '../../api/deliveryLocations'

const { useBreakpoint } = Grid

export default function AdminCaptains() {
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [addOpen, setAddOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [locations, setLocations] = React.useState([])
  const [locationsLoading, setLocationsLoading] = React.useState(false)
  const [form] = Form.useForm()

  async function load() {
    setLoading(true)
    try {
      const rows = await listCaptains()
      setData(Array.isArray(rows) ? rows : [])
    } catch (e) {
      console.error(e)
      message.error('فشل في تحميل الكباتن')
    } finally {
      setLoading(false)
    }
  }
  React.useEffect(() => { load() }, [])

  async function openAdd() {
    setAddOpen(true)
    form.resetFields()
    if (locations.length === 0) {
      setLocationsLoading(true)
      try {
        const locs = await listDeliveryLocations()
        setLocations(Array.isArray(locs) ? locs : [])
      } catch (e) {
        console.error(e)
      } finally {
        setLocationsLoading(false)
      }
    }
  }

  async function addCaptain() {
    let values
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    try {
      const r = await createCaptain({
        phone: values.phone.trim(),
        name: values.name.trim(),
        preferredLocationIds: Array.isArray(values.preferredLocationIds) ? values.preferredLocationIds : [],
      })
      if (!r.ok) {
        // The global interceptor already toasts the raw BE error code; map the
        // common ones to friendlier Arabic copy here. skipGlobalErrorToast on
        // the call would suppress the generic toast — but for now we let both
        // appear so the admin still sees the raw code if there's a new one.
        const code = r.body?.error
        if (code === 'already_captain') message.error('هذا المستخدم كابتن بالفعل')
        else if (code === 'user_inactive') message.error('المستخدم معطّل، لا يمكن تعيينه ككابتن')
        else if (code === 'unknown_delivery_location_id') message.error('إحدى المناطق المختارة غير معروفة')
        return
      }
      message.success('تمت الإضافة')
      setAddOpen(false)
      form.resetFields()
      await load()
    } finally {
      setSaving(false)
    }
  }

  const columns = [
    { title: 'المعرف', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'الاسم', dataIndex: 'name', key: 'name' },
    { title: 'الهاتف', dataIndex: 'phone', key: 'phone' },
    {
      title: 'فعّال',
      dataIndex: 'isCaptain',
      key: 'isCaptain',
      render: v => v ? <Tag color="green">نعم</Tag> : <Tag color="red">لا</Tag>,
    },
    {
      title: 'إجراءات',
      key: 'actions',
      render: (_, r) => (
        <Space size="small" wrap>
          <Link to={`/admin/captains/${r.id}`}>
            <Button size={isMobile ? 'small' : 'middle'}>تعديل</Button>
          </Link>
          <Link to={`/admin/captains/${r.id}/dashboard`}>
            <Button size={isMobile ? 'small' : 'middle'}>لوحة</Button>
          </Link>
        </Space>
      ),
    },
  ]

  const locationOptions = locations.map(l => ({
    value: l.id,
    label: `${l.nameAr || l.name || `#${l.id}`}${l.isCustomerVisible === false ? ' (مخفية)' : ''}`,
  }))

  return (
    <div
      dir="rtl"
      style={{
        maxWidth: 1200,
        margin: isMobile ? '12px auto' : '24px auto',
        padding: isMobile ? '0 8px' : '0 12px',
      }}
    >
      <Card
        title="إدارة الكباتن"
        styles={{ body: { padding: isMobile ? 8 : 24 } }}
        extra={
          <Space wrap size="small">
            <Button onClick={load} size={isMobile ? 'small' : 'middle'}>تحديث</Button>
            <Button
              type="primary"
              onClick={openAdd}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? 'إضافة' : 'إضافة كابتن'}
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          dataSource={data}
          columns={columns}
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: isMobile ? 10 : 25, simple: isMobile }}
        />
      </Card>

      <Modal
        open={addOpen}
        title="إضافة كابتن"
        okText="إضافة"
        cancelText="إلغاء"
        onOk={addCaptain}
        confirmLoading={saving}
        onCancel={() => { setAddOpen(false); form.resetFields() }}
        width={isMobile ? '95vw' : 560}
        style={isMobile ? { top: 12, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflowY: 'auto' } } : undefined}
        centered={!isMobile}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="phone"
            label="رقم الهاتف"
            rules={[
              { required: true, message: 'الهاتف مطلوب' },
              { pattern: /^05\d{8}$/, message: 'يجب أن يبدأ بـ 05 ويتكون من 10 أرقام' },
            ]}
          >
            <Input placeholder="05XXXXXXXX" inputMode="tel" />
          </Form.Item>
          <Form.Item
            name="name"
            label="الاسم"
            rules={[{ required: true, message: 'الاسم مطلوب' }]}
          >
            <Input placeholder="اسم الكابتن" />
          </Form.Item>
          <Form.Item
            name="preferredLocationIds"
            label="المناطق المفضّلة (اختياري)"
            tooltip="عند ورود طلب لإحدى هذه المناطق سيتم إعلام هذا الكابتن أولاً. اتركها فارغة لاستلام البث العام فقط."
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="اختر المناطق التي يفضّلها الكابتن"
              loading={locationsLoading}
              options={locationOptions}
            />
          </Form.Item>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            إذا لم يكن المستخدم مسجّلاً بعد في التطبيق، سيتم إنشاء حسابه بهذا الاسم والرقم تلقائياً. عند أول تسجيل دخول له بـ OTP، سيتم دمج الحساب وتفعيل قدرته على استلام الطلبات.
          </Typography.Text>
        </Form>
      </Modal>
    </div>
  )
}
